import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'store';
import { Crawler } from 'types/crawler';
import { unionBy } from '../../../utils/arrayUtils';

/**
 * クローラー一覧画面の状態
 */
interface ListState {
  /**
   * ロード中フラグ
   */
  loading: boolean;
  /**
   * クローラー一覧
   */
  crawlers: Crawler[];
  /**
   * 選択されたクローラー
   */
  selectedCrawler: Crawler | undefined;
  /**
   * 検索フィールドの値
   */
  searchValue: string;
}

/**
 * クローラー一覧画面の初期状態
 */
const initialState: ListState = {
  loading: false,
  crawlers: [],
  searchValue: '',
  selectedCrawler: undefined,
};

/**
 * クローラー画面の slice(reducer + action)
 */
export const listSlice = createSlice({
  name: 'listCrawler',
  initialState,
  reducers: {
    init: () => initialState,
    loadStarted: (state) => {
      state.loading = true;
    },
    loadEnded: (state) => {
      state.loading = false;
    },
    fetchCrawlersSuccess: (
      state,
      action: PayloadAction<{
        crawlers: Crawler[];
      }>,
    ) => {
      const { crawlers } = action.payload;
      state.crawlers = crawlers;
    },
    searchValueChanged: (state, action: PayloadAction<{ value: string }>) => {
      state.searchValue = action.payload.value;
    },
  },
});

/**
 * クローラー一覧画面の action
 */
export const listActions = listSlice.actions;

/**
 * クローラー一覧画面の selector（内部用）
 */
const getloading = (state: RootState) => state.crawler.list.loading;

const getCrawlers = (state: RootState) => state.crawler.list.crawlers;

const getSearchValue = (state: RootState) => state.crawler.list.searchValue;

const getFilteredCrawlers = createSelector(
  getCrawlers,
  getSearchValue,
  (crawlers, searchValue) =>
    [
      // クローラーIDに部分一致
      crawlers.filter(({ id }) => id.toString().indexOf(searchValue) !== -1),
      // クローラー名に部分一致
      crawlers.filter(
        ({ name }) =>
          name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1,
      ),
      // イメージ名に部分一致
      crawlers.filter(
        ({ imageName }) =>
          imageName.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1,
      ),
    ].reduce((prev, cur) => unionBy(prev, cur, 'id'), []),
);

/**
 * クローラー一覧画面の selector
 */
export const listSelectors = {
  loading: getloading,
  filteredCrawlers: getFilteredCrawlers,
  crawlers: getCrawlers,
  searchValue: getSearchValue,
} as const;
